import React from 'react';
import { Mail, Phone, MapPin } from 'lucide-react';

// Client-side approximation of the resume so users see progress as they type.
// The authoritative output is the backend-generated PDF. Always rendered on
// white like paper, independent of app dark mode.
export default function LivePreview({ state }) {
  const profile = state?.profile || {};
  const experiences = (state?.experiences || []).filter((e) => e && (e.title || e.company));
  const educations = (state?.educations || []).filter((e) => e && (e.degree || e.institution));
  const projects = (state?.projects || []).filter((p) => p && (p.name || p.description));
  const certifications = (state?.certifications || []).filter((c) => c && c.name);
  const achievements = (state?.achievements || []).filter((a) => a && typeof a === 'string' && a.trim());

  const Section = ({ title, children }) => (
    <div className="mt-4">
      <h3 className="text-[11px] font-bold uppercase tracking-[0.12em] text-gray-700 border-b border-gray-300 pb-1 mb-2">
        {title}
      </h3>
      {children}
    </div>
  );

  return (
    <div className="bg-white text-gray-900 rounded-xl shadow-xl border border-gray-200 p-6 text-[13px] leading-relaxed min-h-[420px]">
      <h2 className="text-xl font-bold tracking-tight">{profile.name || 'Your Name'}</h2>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-[11px] text-gray-600">
        {profile.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{profile.email}</span>}
        {profile.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{profile.phone}</span>}
        {profile.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{profile.location}</span>}
      </div>

      {profile.summary && <p className="mt-3 text-gray-700">{profile.summary}</p>}

      {experiences.length > 0 && (
        <Section title="Work Experience">
          {experiences.map((e, i) => (
            <div key={i} className="mb-2.5">
              <div className="flex justify-between gap-2">
                <span className="font-semibold">{e.title}{e.company ? ` · ${e.company}` : ''}</span>
                <span className="text-[11px] text-gray-500 whitespace-nowrap">
                  {e.start}{(e.start || e.end || e.current) ? ' – ' : ''}{e.current ? 'Present' : e.end}
                </span>
              </div>
              {e.description && <p className="text-gray-700 mt-0.5">{e.description}</p>}
            </div>
          ))}
        </Section>
      )}

      {educations.length > 0 && (
        <Section title="Education">
          {educations.map((e, i) => (
            <div key={i} className="flex justify-between gap-2 mb-1.5">
              <span><span className="font-semibold">{e.degree}</span>{e.institution ? `, ${e.institution}` : ''}{e.score ? ` — ${e.score}` : ''}</span>
              <span className="text-[11px] text-gray-500">{e.year}</span>
            </div>
          ))}
        </Section>
      )}

      {state.skills.length > 0 && (
        <Section title="Skills">
          <div className="flex flex-wrap gap-1.5">
            {state.skills.map((s) => (
              <span key={s} className="px-2 py-0.5 bg-gray-100 rounded text-[11px]">{s}</span>
            ))}
          </div>
        </Section>
      )}

      {projects.length > 0 && (
        <Section title="Projects">
          {projects.map((p, i) => (
            <div key={i} className="mb-2">
              <span className="font-semibold">{p.name}</span>
              {p.description && <p className="text-gray-700 mt-0.5">{p.description}</p>}
            </div>
          ))}
        </Section>
      )}

      {certifications.length > 0 && (
        <Section title="Certifications">
          {certifications.map((c, i) => (
            <p key={i} className="mb-1">{c.name}{c.issuer ? ` — ${c.issuer}` : ''}{c.year ? ` (${c.year})` : ''}</p>
          ))}
        </Section>
      )}

      {state.languages.length > 0 && (
        <Section title="Languages">
          <p>{state.languages.join(', ')}</p>
        </Section>
      )}

      {achievements.length > 0 && (
        <Section title="Achievements">
          <ul className="list-disc pl-4">
            {achievements.map((a, i) => <li key={i}>{a}</li>)}
          </ul>
        </Section>
      )}
    </div>
  );
}
